

import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Paper,
  CircularProgress,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Logo1 from '../../src/landpageimage/logo1.png';
import Logo2 from '../../src/landpageimage/TDTLLogo.png';
import Logo3 from '../../src/landpageimage/logo2.jpg';
import Logo4 from '../landpageimage1/DYPLogo.png';

const themeColors = {
  primary: '#2196F3',
  gradient: 'linear-gradient(135deg, #2196F3 0%, #6633EE 100%)',
  background: 'radial-gradient(217.23% 100.08% at 50% 18.33%, rgba(0, 0, 0, 0) 21.67%, #6633EE 69.34%, rgb(15, 19, 26) 100%)',
};

const StyledPaper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '90%',
  maxWidth: '1200px',
  margin: '0 auto',
  borderRadius: '24px',
  padding: '16px',
  backgroundColor: '#FFFFFF',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#F3F4F6',
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
    '&.Mui-focused fieldset': {
      borderColor: themeColors.primary,
    },
  },
}));

const RegisterButton = styled(Button)(() => ({
  background: themeColors.gradient,
  color: 'white',
  padding: '8px',
  borderRadius: '16px',
  fontWeight: 600,
  '&:hover': {
    background: '#1E88E5',
  },
}));

export default function InvestorRegister() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    phone_number: '',
    organisation: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    gender: '',
    domain: '',
    linked_url: '',
    role: 'Investor',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const LogoContainer = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '16px',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  padding: '16px 0',
  marginBottom: '16px',
  '@media (max-width: 600px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
}));

const LogoImage = styled('img')(() => ({
  maxWidth: '100px',
  maxHeight: '80px',
  objectFit: 'contain',
  margin: '0 auto',
}));


  const validateField = (name, value) => {
    switch (name) {
      case 'full_name':
        return /^[a-zA-Z ]+$/.test(value) ? '' : 'Full Name should only contain letters and spaces.';
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Invalid email address.';
      case 'phone_number':
        return /^[0-9]{10}$/.test(value) ? '' : 'Phone Number should be exactly 10 digits.';
      case 'postal_code':
        return /^[0-9]{6}$/.test(value) ? '' : 'Postal Code should be exactly 6 digits.';
      case 'password':
        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/.test(value)
          ? ''
          : 'Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character.';
      case 'confirm_password':
        return value === formData.password ? '' : 'Passwords do not match.';
      case 'linked_url':
        return /^(https?:\/\/)?([\w\d-]+\.){1,2}[\w\d]{2,}(\/.*)?$/.test(value)
          ? ''
          : 'Invalid LinkedIn URL.';
      case 'country':
        return /^[a-zA-Z ]+$/.test(value) ? '' : 'Country should only contain letters and spaces.';
      case 'city':
        return /^[a-zA-Z ]+$/.test(value) ? '' : 'City should only contain letters and spaces.';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrors((prevState) => ({
      ...prevState,
      [name]: validateField(name, value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      newErrors[key] = validateField(key, formData[key]);
    });
    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      Swal.fire('Error', 'Please fix the errors in the form before submitting.', 'error');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('https://newbackend.elephant-tank.com/api/register_inve/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();

      if (response.ok) {
        Swal.fire({
          title: 'Success!',
          text: 'Verify OTP for Confirmation of Registration.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          willClose: () => navigate('/Otpverificationinves', { state: { email: formData.email } }),
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Registration failed!',
          icon: 'error',
          confirmButtonText: 'Try Again',
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'An error occurred. Please try again.',
        icon: 'error',
        confirmButtonText: 'Close',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: themeColors.background,
      padding: '16px',
    }}
    >
      <StyledPaper>
        <LogoContainer>
          <LogoImage src={Logo1} alt="Logo 1" />
          <LogoImage src={Logo2} alt="Logo 2" />
          <LogoImage src={Logo3} alt="Logo 3" />
          <LogoImage src={Logo4} alt="Logo 4" />
        </LogoContainer>
        <Typography
  variant="h4"
  fontWeight="bold"
  mb={3}
  sx={{
    position: 'relative',
    overflow: 'hidden',
    textAlign: 'center',
    fontSize: '2rem',
    lineHeight: 1.5,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: 'transparent',
    backgroundImage: themeColors.gradient,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    animation: 'slideIn 1.5s ease-out',
    '@keyframes slideIn': {
      '0%': {
        transform: 'translateY(-50px)',
        opacity: 0,
      },
      '100%': {
        transform: 'translateY(0)',
        opacity: 1,
      },
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, rgba(255,255,255,0.2), rgba(255,255,255,0.6), rgba(255,255,255,0.2))',
      transform: 'translateX(-100%)',
      animation: 'shimmer 2s infinite',
    },
    '@keyframes shimmer': {
      '0%': {
        transform: 'translateX(-100%)',
      },
      '100%': {
        transform: 'translateX(100%)',
      },
    },
  }}
>
  Welocome Investors!!! <br />
  <Typography
    component="span"
    variant="h5"
    fontWeight="normal"
    sx={{
      color: '#2196F3',
      textShadow: '0px 0px 10px rgba(0, 0, 0, 0.2)',
      fontStyle: 'italic',
      animation: 'pulse 2s infinite',
      '@keyframes pulse': {
        '0%': {
          transform: 'scale(1)',
          opacity: 1,
        },
        '50%': {
          transform: 'scale(1.05)',
          opacity: 0.9,
        },
        '100%': {
          transform: 'scale(1)',
          opacity: 1,
        },
      },
    }}
  >
    Empowering Investors to Build Wealth and Transform Industries
  </Typography>
</Typography>

        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Grid container spacing={2}>
            {Object.entries({
              full_name: 'Full Name',
              email: 'Email',
              phone_number: 'Phone Number',
              organisation: 'Organisation',
              address: 'Address',
              city: 'City',
              state: 'State',
              postal_code: 'Postal Code',
              country: 'Country',
              linked_url: 'LinkedIn URL',
            }).map(([name, label]) => (
              <Grid item xs={12} sm={4} key={name}>
                <StyledTextField
                  fullWidth
                  label={label}
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  error={!!errors[name]}
                  helperText={errors[name]}
                  required
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={4}>
              <StyledTextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StyledTextField
                fullWidth
                label="Confirm Password"
                name="confirm_password"
                type="password"
                value={formData.confirm_password}
                onChange={handleChange}
                error={!!errors.confirm_password}
                helperText={errors.confirm_password}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  error={!!errors.gender}
                  required
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Domain</InputLabel>
                <Select
                  name="domain"
                  value={formData.domain}
                  onChange={handleChange}
                  error={!!errors.domain}
                  required
                >
                  <MenuItem value="AI/ML">AI/ML</MenuItem>
                      <MenuItem value="Technology">Technology</MenuItem>
                      <MenuItem value="Real Estate">Real Estate</MenuItem>
                      <MenuItem value="Healthcare and Biotech">Healthcare and Biotech</MenuItem>
                      <MenuItem value="Pharmacy ">Pharmacy </MenuItem>
                      <MenuItem value="E-commerce and Retail">E-commerce and Retail</MenuItem>
                      <MenuItem value="Education">Education</MenuItem>
                      <MenuItem value="Finance and FinTech">Finance and FinTech</MenuItem>
                      <MenuItem value="Energy and Sustainability">Energy and Sustainability</MenuItem>
                      <MenuItem value="Entertainment and Media">Entertainment and Media</MenuItem>
                      <MenuItem value="Agriculture and Food">Agriculture and Food</MenuItem>
                      <MenuItem value="Fashion and Lifestyle">Fashion and Lifestyle</MenuItem>
                      <MenuItem value="Others">Others</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <RegisterButton type="submit" fullWidth disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Register'}
              </RegisterButton>
            </Grid>
          </Grid>
        </form>
      </StyledPaper>
    </Box>
  );
}